import Vue from 'vue';
import _ from 'lodash';
import { defineStore } from 'pinia';
import uniqid from 'uniqid';

import EventApi from '@/extensions/eventManager/store/api/event';
import CategoryApi from '@/extensions/eventManager/store/api/category';
import PageApi from '@/extensions/eventManager/store/api/page';
import RegistrationTemplateApi from '@/extensions/eventManager/store/api/registrationTemplate';
import MenuApi from '@/store/api/cms/menu';
import { useSetupStore } from '@/stores';

const form = {
    id: undefined,
    name: '',
    description: '',
    color: null,
    booking_notification_text: '',
    use_qr_code: true,
    registrationTemplate: null,
    banner: null,
    banner_id: null,
    category: {
        id: undefined,
        name: ''
    },
    address: {
        id: undefined,
        company: '',
        street: '',
        zipcode: '',
        city: '',
        country: ''
    },
    slots: []
};

const rules = {
    name: 'required',
    category: 'required',
};

const ui = {
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

export const useEventManagerEventStore = defineStore('eventManagerEvent', {
    state: () => ({
        loading: true,
        eventsPaginated: {
            path: null,
            variables: {
                // order: 'id',
                // direction: 'asc',
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            category: {
                loading: false,
                items: []
            },
            event: {
                loading: false,
                items: []
            },
            menu: {
                loading: false,
                items: []
            },
            registrationTemplate: {
                loading: false,
                items: []
            }
        },
        form: _.cloneDeep(form),
        rules: _.cloneDeep(rules),
        ui: _.cloneDeep(ui)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        // List mutations converted to actions
        addPaginatedEventsToState(data) {
            this.eventsPaginated.items = data.items;
            this.eventsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.eventsPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.eventsPaginated.link = _.cloneDeep(data.config.link);
            this.eventsPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.eventsPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.eventsPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.eventsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.eventsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.eventsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.eventsPaginated.filters, key);
            } else {
                Vue.set(this.eventsPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            this.eventsPaginated.variables.order = key;
            this.eventsPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.eventsPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(rules) {
            this.rules = rules;
        },

        resetRules() {
            this.rules = _.cloneDeep(rules);
        },

        resetUI() {
            this.ui = _.cloneDeep(ui);
        },

        insertEvent(event) {
            this.form = event;
        },

        updateCategoryOptionsLoading(value) {
            this.options.category.loading = value;
        },

        insertCategoryOptions(categories) {
            this.options.category.items = categories;
        },

        updateEventOptionsLoading(value) {
            this.options.event.loading = value;
        },

        insertEventOptions(events) {
            this.options.event.items = events;
        },

        updateMenuOptionsLoading(value) {
            this.options.menu.loading = value;
        },

        insertMenuOptions(menus) {
            this.options.menu.items = menus;
        },

        // Form UI actions
        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        // Form field setters
        setFormName(value) {
            this.form.name = value;
        },

        setFormDescription(value) {
            this.form.description = value;
        },

        setFormColor(value) {
            this.form.color = value;
        },

        setFormBooking(value) {
            Vue.set(this.form, 'booking', value);
        },

        setFormMemberLimitPerSlot(value) {
            Vue.set(this.form, 'member_limit_per_slot', parseInt(value));
        },

        // people one registration may bring, the booker included - never below 1
        setFormMaxPersonsPerBooking(value) {
            Vue.set(this.form, 'max_persons_per_booking', Math.max(1, parseInt(value) || 1));
        },

        setFormShowSeatsLeft(value) {
            Vue.set(this.form, 'show_seats_left', !!value);
        },

        // from how many seats left on the number is shown; empty = whenever there is a limit
        setFormSeatsLeftFrom(value) {
            const from = parseInt(value);
            Vue.set(this.form, 'seats_left_from', from > 0 ? from : null);
        },

        setFormUseQrCode(value) {
            Vue.set(this.form, 'use_qr_code', value);
        },

        // { id, name } or null (= fixed fields only)
        setFormRegistrationTemplate(template) {
            Vue.set(this.form, 'registrationTemplate', template ? _.pick(template, ['id', 'name']) : null);
        },

        updateRegistrationTemplateOptionsLoading(value) {
            Vue.set(this.options.registrationTemplate, 'loading', value);
        },

        insertRegistrationTemplateOptions(templates) {
            Vue.set(this.options.registrationTemplate, 'items', templates);
        },
        setFormBanner(upload) {
            Vue.set(this.form, 'banner', upload);
            Vue.set(this.form, 'banner_id', upload?.id || null);
        },
        // Nicht-destruktiver Zuschnitt: nur settings.processing des Banners setzen,
        // banner_id bleibt unverändert. null/leer = Zuschnitt entfernen (Original).
        setFormBannerProcessing(processing) {
            if (!this.form.banner) return;
            const settings = { ...(this.form.banner.settings || {}), processing: processing || null };
            Vue.set(this.form.banner, 'settings', settings);
        },

        setFormBookingNotificationText(value) {
            this.form.booking_notification_text = value;
        },

        setFormCompany(value) {
            this.form.address.company = value;
        },

        setFormStreet(value) {
            this.form.address.street = value;
        },

        setFormZipcode(value) {
            this.form.address.zipcode = value;
        },

        setFormCity(value) {
            this.form.address.city = value;
        },

        setFormCountry(value) {
            this.form.address.country = value;
        },

        setFormCategory(value) {
            this.form.category = value;
        },

        // Slot actions
        addFormSlot(slot) {
            // check if slots exists
            if (!this.form.slots) {
                this.form.slots = [];
            }

            slot = _.cloneDeep(slot);
            slot.id = uniqid('new:');

            this.form.slots.push(slot);
        },

        updateFormSlot(slot) {
            const id = slot.id;
            const slots = _.get(this, 'form.slots') || [];
            const i = _.findIndex(slots, s => s.id === id);

            if (i >= 0) {
                slot = _.cloneDeep(slot);
                Vue.set(this.form.slots, i, slot);
            }
        },

        markFormSlotAsDeleted(slot) {
            const id = slot.id;
            const slots = _.get(this, 'form.slots') || [];
            const i = _.findIndex(slots, s => s.id === id);

            if (i >= 0) {
                Vue.set(this.form.slots[i], 'deleted', true);
            }
        },

        unmarkFormSlotAsDeleted(slot) {
            const id = slot.id;
            const slots = _.get(this, 'form.slots') || [];
            const i = _.findIndex(slots, s => s.id === id);

            if (i >= 0) {
                Vue.set(this.form.slots[i], 'deleted', false);
            }
        },

        duplicateFormSlot(slot) {
            slot = _.cloneDeep(slot);
            slot.id = uniqid('new:');
            // A copy is its own date, not another instance of the run it was copied from -
            // otherwise "delete all dates" would sweep it up too.
            slot.series_key = null;

            const span = slot.end_at - slot.start_at;
            slot.start_at = slot.end_at;
            slot.end_at += span;

            this.form.slots.push(slot);
        },

        // Booking actions
        addFormBooking(booking) {
            const slots = _.get(this, 'form.slots') || [];
            const s = _.findIndex(slots, slot => slot.id === booking.slot.id);

            // check if bookings exists
            if (!this.form.slots[s].bookings) {
                Vue.set(this.form.slots[s], 'bookings', []);
            }

            booking.id = uniqid('new:');

            booking = _.cloneDeep(booking);
            booking.booker.id = uniqid('new:');
            booking.booker.name = `${booking.booker.first_name} ${booking.booker.last_name}`;

            let event = booking.event.name;
            if (booking.event.category) event += ` - ${booking.event.category.name}`;
            booking.event = event;

            this.form.slots[s].bookings.push(_.omit(booking, ['slot', 'originalSlot']));
        },

        updateFormBooking(booking) {
            const slots = _.get(this, 'form.slots') || [];
            const os = _.findIndex(slots, slot => slot.id === booking.originalSlot.id);
            const ns = _.findIndex(slots, slot => slot.id === booking.slot.id);

            const id = booking.id;
            let bookings = _.get(this, `form.slots.${os}.bookings`) || [];
            let i = _.findIndex(bookings, b => b.id === id);

            let s = os;

            if (i >= 0) {
                booking = _.cloneDeep(booking);

                booking.booker.name = `${booking.booker.first_name} ${booking.booker.last_name}`;

                let event = booking.event.name;
                if (booking.event.category) event += ` - ${booking.event.category.name}`;
                booking.event = event;

                if (os !== ns) {
                    s = ns;

                    // remove booking from form.slots[os]
                    this.form.slots[os].bookings.splice(i, 1);

                    // add booking to form.slots[ns]
                    bookings = _.get(this, `form.slots.${ns}.bookings`);
                    if (bookings === undefined) {
                        Vue.set(this.form.slots[ns], 'bookings', []);
                    }
                    i = _.get(this, `form.slots.${ns}.bookings.length`) || 0;
                }

                Vue.set(this.form.slots[s].bookings, i, _.omit(booking, ['slot', 'originalSlot']));
            }
        },

        markFormBookingAsDeleted(booking) {
            const slots = _.get(this, 'form.slots') || [];
            const s = _.findIndex(slots, slot => slot.id === booking.slot.id);

            const id = booking.id;
            const bookings = _.get(this, `form.slots.${s}.bookings`) || [];
            const i = _.findIndex(bookings, b => b.id === id);

            if (i >= 0) {
                Vue.set(this.form.slots[s].bookings[i], 'deleted', true);
            }
        },

        unmarkFormBookingAsDeleted(booking) {
            const slots = _.get(this, 'form.slots') || [];
            const s = _.findIndex(slots, slot => slot.id === booking.slot.id);

            const id = booking.id;
            const bookings = _.get(this, `form.slots.${s}.bookings`) || [];
            const i = _.findIndex(bookings, b => b.id === id);

            if (i >= 0) {
                Vue.set(this.form.slots[s].bookings[i], 'deleted', false);
            }
        },

        // Async actions
        fetchEventsPaginated() {
            return EventApi.fetchEventsPaginated(this.eventsPaginated).then(data => {
                this.addPaginatedEventsToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        detachTicketProduct(slotId, productId) {
            return EventApi.detachTicketProduct(slotId, productId);
        },

        fetchEvent(id) {
            return EventApi.fetchEvent(id).then(event => {
                if (!_.get(event, 'address.id')) {
                    event.address = {
                        id: uniqid('new:'),
                        company: '',
                        street: '',
                        zipcode: '',
                        city: '',
                        country: ''
                    };
                }

                this.insertEvent(_.cloneDeep(event));
            });
        },

        fetchCategoryOptions(cmsID) {
            this.updateCategoryOptionsLoading(true);
            return CategoryApi.fetchList(cmsID)
                .then(categories => {
                    this.updateCategoryOptionsLoading(false);
                    this.insertCategoryOptions(categories);
                })
                .catch(err => {
                    this.updateCategoryOptionsLoading(false);
                    throw err;
                });
        },

        fetchEventOptions(cmsID) {
            this.updateEventOptionsLoading(true);
            return EventApi.fetchList(cmsID)
                .then(events => {
                    this.updateEventOptionsLoading(false);
                    this.insertEventOptions(events);
                })
                .catch(err => {
                    this.updateEventOptionsLoading(false);
                    throw err;
                });
        },

        fetchRegistrationTemplateOptions(cmsID) {
            this.updateRegistrationTemplateOptionsLoading(true);
            return RegistrationTemplateApi.fetchList(cmsID)
                .then(templates => {
                    this.updateRegistrationTemplateOptionsLoading(false);
                    this.insertRegistrationTemplateOptions(templates);
                })
                .catch(err => {
                    this.updateRegistrationTemplateOptionsLoading(false);
                    throw err;
                });
        },

        fetchMenuOptions(cmsID) {
            this.updateMenuOptionsLoading(true);
            return MenuApi.fetchList(cmsID)
                .then(menus => {
                    this.updateMenuOptionsLoading(false);
                    this.insertMenuOptions(menus);
                })
                .catch(err => {
                    this.updateMenuOptionsLoading(false);
                    throw err;
                });
        },

        findCategoryPosts(query) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            const categories = _.get(cms, 'config.template.categories') || [];
            return PageApi.fetchPagesPaginated({
                pageInfo: { perPage: 100 },
                variables: {
                    order: 'created_at',
                    direction: 'desc'
                },
                filters: [
                    { key: 'title', like: query },
                    { key: 'cms', match: cms.id },
                    { key: 'category_keys', in: categories.map(el => el.key) }
                ]
            });
        },

        create(event) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            event.cms = { id: cms.id };

            return EventApi.create(event).then(event => {
                this.insertEvent(_.cloneDeep(event));
                this.resetRules();
                return event;
            });
        },

        update(event) {
            event = _.cloneDeep(event);
            event.slots = event.slots.map(slot => {
                if (slot.hyperlink && slot.hyperlink.page) {
                    slot.hyperlink.page = {
                        id: slot.hyperlink.page.id
                    };
                }
                return slot;
            });

            return EventApi.update(event).then(event => {
                this.insertEvent(_.cloneDeep(event));
                this.resetRules();
                return event;
            });
        },

        deleteEvent(id) {
            return EventApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        reset() {
            this.insertEvent(_.cloneDeep(form));
        }
    }
});
